/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense } from "react";
import { RoleGate } from "./components/role-gate";
import { Loading } from "./components/ui/loading";
import { ROLE } from "./utils/constant";
import { keycloak } from "./utils/lib/keycloak";
import Preview from "./pages/preview/Preview";
import { e2eSplitRoutes } from "./e2e-split.routes";

const hideFeatures = import.meta.env.VITE_HIDE_FEATURES?.split(",") ?? [];

// Microfrontends

// Funnel Module
const UploadGroupingFunnel = lazy(() => import("e2e/UploadGroupingFunnel"));
const UpdateStatusGroupingFunnel = lazy(
  () => import("e2e/UpdateStatusGroupingFunnel"),
);
const FunnelDetail = lazy(() => import("e2e/FunnelDetail"));
const FunnelIndex = lazy(() => import("e2e/FunnelIndex"));
const SuffixList = lazy(() => import("e2e/SuffixList"));
const SuffixDetail = lazy(() => import("e2e/SuffixDetail"));

// Permit Module
const PermitProgress = lazy(() => import("e2e/PermitProgress"));
const PermitProgessDetail = lazy(() => import("e2e/PermitProgressDetail"));
const AssignVendor = lazy(() => import("e2e/AssignVendor"));
const AssignVendorDetail = lazy(() => import("e2e/AssignVendorDetail"));
const ReviewCompensationDispatch = lazy(
  () => import("e2e/ReviewCompensationDispatch"),
);
const ReviewCompensationDispatchDetail = lazy(
  () => import("e2e/ReviewCompensationDispatchDetail"),
);
const CompensationApprovalMonitoring = lazy(
  () => import("e2e/CompensationApprovalMonitoring"),
);
const CompensationApprovalMonitoringDetail = lazy(
  () => import("e2e/CompensationApprovalMonitoringDetail"),
);
const CompensationApprovalProjectOperationLead = lazy(
  () => import("e2e/CompensationApprovalProjectOperationLead"),
);
const CompensationApprovalProjectOperationLeadDetail = lazy(
  () => import("e2e/CompensationApprovalProjectOperationLeadDetail"),
);
const CompensationApprovalProjectManager = lazy(
  () => import("e2e/CompensationApprovalProjectManager"),
);
const CompensationApprovalProjectManagerDetail = lazy(
  () => import("e2e/CompensationApprovalProjectManagerDetail"),
);
const RequestExpansionFunnelProject = lazy(
  () => import("e2e/RequestExpansionFunnelProject"),
);
const RequestExpansionFunnelProjectDetail = lazy(
  () => import("e2e/RequestExpansionFunnelProjectDetail"),
);
const RequestExpansionFunnelManagement = lazy(
  () => import("e2e/RequestExpansionFunnelManagement"),
);
const RequestExpansionFunnelManagementDetail = lazy(
  () => import("e2e/RequestExpansionFunnelManagementDetail"),
);
const ValidatePermitRejectMonitoring = lazy(
  () => import("e2e/ValidatePermitRejectMonitoring"),
);
const ValidatePermitRejectMonitoringDetail = lazy(
  () => import("e2e/ValidatePermitRejectMonitoringDetail"),
);
const ValidatePermitRejectDispatch = lazy(
  () => import("e2e/ValidatePermitRejectDispatch"),
);
const ValidatePermitRejectDispatchDetail = lazy(
  () => import("e2e/ValidatePermitRejectDispatchDetail"),
);
const RePermitVendor = lazy(() => import("e2e/RePermitVendor"));
const RePermitVendorDetail = lazy(() => import("e2e/RePermitVendorDetail"));
const RequestOpenAccessFunnelProject = lazy(
  () => import("e2e/RequestOpenAccessFunnelProject"),
);
const RequestOpenAccessFunnelProjectDetail = lazy(
  () => import("e2e/RequestOpenAccessFunnelProjectDetail"),
);
const RequestRePermitFunnelProject = lazy(
  () => import("e2e/RequestRePermitFunnelProject"),
);
const RequestRePermitFunnelProjectDetail = lazy(
  () => import("e2e/RequestRePermitFunnelProjectDetail"),
);
const ChangeRequestRePermitFunnelManagement = lazy(
  () => import("e2e/ChangeRequestRePermitFunnelManagement"),
);
const ChangeRequestRePermitFunnelManagementDetail = lazy(
  () => import("e2e/ChangeRequestRePermitFunnelManagementDetail"),
);
const MonitoringSuffix = lazy(() => import("e2e/MonitoringSuffix"));
const MonitoringSuffixDetail = lazy(() => import("e2e/MonitoringSuffixDetail"));

// Design Module
const NetworkTypeLMFeederList = lazy(
  () => import("e2e/NetworkTypeLMFeederList"),
);
const NetworkTypeLMFeederDetail = lazy(
  () => import("e2e/NetworkTypeLMFeederDetail"),
);
const ApdVendorList = lazy(() => import("e2e/ApdVendorList"));
const ApdVendorDetail = lazy(() => import("e2e/ApdVendorDetail"));
const ApdLMFeederList = lazy(() => import("e2e/ApdLMFeederList"));
const RequestCodeFdtOlt = lazy(() => import("e2e/RequestCodeFdtOlt"));
const DefinePermitFeeder = lazy(() => import("e2e/DefinePermitFeeder"));
const DefinePermitFeederDetail = lazy(
  () => import("e2e/DefinePermitFeederDetail"),
);
const RequestReboundary = lazy(() => import("e2e/RequestReboundary"));
const RequestReboundaryCreate = lazy(
  () => import("e2e/RequestReboundaryCreate"),
);
const RequestReboundaryDetail = lazy(
  () => import("e2e/RequestReboundaryDetail"),
);
const Reboundary = lazy(() => import("e2e/Reboundary"));
const ReboundaryDetail = lazy(() => import("e2e/ReboundaryDetail"));
const DashboardLinknetAPD = lazy(() => import("e2e/DashboardLinknetAPD"));
const RollbackAllList = lazy(() => import("e2e/RollbackAllList"));
const RollbackAllDetail = lazy(() => import("e2e/RollbackAllDetail"));
const RollbackAPDLMDetail = lazy(() => import("e2e/RollbackAPDLMDetail"));

// WO Management Module
const WONosaList = lazy(() => import("e2e/WONosaList"));
const WONosaCreate = lazy(() => import("e2e/WONosaCreate"));
const WONosaDetail = lazy(() => import("e2e/WONosaDetail"));
const WOEnterpriseList = lazy(() => import("e2e/WOEnterpriseList"));
const WOEnterpriseCreate = lazy(() => import("e2e/WOEnterpriseCreate"));
const WOEnterpriseDetail = lazy(() => import("e2e/WOEnterpriseDetail"));

export const e2eRoutes = [
  {
    path: "funnel",
    children: [
      {
        path: "funnel",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.FUNNEL_MANAGEMENT, ROLE.FUNNEL_VIEW]}>
                  <FunnelIndex keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.FUNNEL_MANAGEMENT, ROLE.FUNNEL_VIEW]}>
                  <FunnelDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "upload-grouping-funnel",
        element: (
          <Suspense fallback={<Loading />}>
            <UploadGroupingFunnel keycloak={keycloak} />
          </Suspense>
        ),
      },
      {
        path: "update-status-permit",
        element: (
          <Suspense fallback={<Loading />}>
            <RoleGate roles={[ROLE.FUNNEL_MANAGEMENT]}>
              <UpdateStatusGroupingFunnel keycloak={keycloak} />
            </RoleGate>
          </Suspense>
        ),
      },
      {
        path: "suffix",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate
                  roles={[
                    ROLE.FUNNEL_MANAGEMENT,
                    ROLE.AGG_HLD,
                    ROLE.FUNNEL_VIEW,
                  ]}
                >
                  <SuffixList keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate
                  roles={[
                    ROLE.FUNNEL_MANAGEMENT,
                    ROLE.AGG_HLD,
                    ROLE.FUNNEL_VIEW,
                  ]}
                >
                  <SuffixDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: "permit-lastmile",
    children: [
      {
        path: "permit-progress",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.VENDOR, ROLE.VENDOR_VIEW]}>
                  <PermitProgress keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.VENDOR, ROLE.VENDOR_VIEW]}>
                  <PermitProgessDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "assign-vendor",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.FUNNEL_PROJECT, ROLE.ADMINISTRATOR]}>
                  <AssignVendor keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.FUNNEL_PROJECT, ROLE.ADMINISTRATOR]}>
                  <AssignVendorDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "review-compensation-permit-dispatch",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.PERMIT_DISPATCH, ROLE.PERMIT_VIEW]}>
                  <ReviewCompensationDispatch keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.PERMIT_DISPATCH, ROLE.PERMIT_VIEW]}>
                  <ReviewCompensationDispatchDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "compensation-approval-permit-monitoring",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.PERMIT_MONITORING]}>
                  <CompensationApprovalMonitoring keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.PERMIT_MONITORING]}>
                  <CompensationApprovalMonitoringDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "compensation-approval-project-operation-lead",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.PROJECT_OPERATIONAL_LEAD]}>
                  <CompensationApprovalProjectOperationLead
                    keycloak={keycloak}
                  />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.PROJECT_OPERATIONAL_LEAD]}>
                  <CompensationApprovalProjectOperationLeadDetail
                    keycloak={keycloak}
                  />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "compensation-approval-project-manager",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.PROJECT_MANAGER]}>
                  <CompensationApprovalProjectManager keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.PROJECT_MANAGER]}>
                  <CompensationApprovalProjectManagerDetail
                    keycloak={keycloak}
                  />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "request-expansion-funnel-project",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.FUNNEL_PROJECT]}>
                  <RequestExpansionFunnelProject keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.FUNNEL_PROJECT]}>
                  <RequestExpansionFunnelProjectDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "request-expansion-funnel-management",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.FUNNEL_MANAGEMENT]}>
                  <RequestExpansionFunnelManagement keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.FUNNEL_MANAGEMENT]}>
                  <RequestExpansionFunnelManagementDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "validate-permit-reject-permit-monitoring",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.ADMINISTRATOR, ROLE.PERMIT_MONITORING]}>
                  <ValidatePermitRejectMonitoring keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.ADMINISTRATOR, ROLE.PERMIT_MONITORING]}>
                  <ValidatePermitRejectMonitoringDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "validate-permit-reject-permit-dispatch",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate
                  roles={[
                    ROLE.ADMINISTRATOR,
                    ROLE.PERMIT_DISPATCH,
                    ROLE.PERMIT_VIEW,
                  ]}
                >
                  <ValidatePermitRejectDispatch keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate
                  roles={[
                    ROLE.ADMINISTRATOR,
                    ROLE.PERMIT_DISPATCH,
                    ROLE.PERMIT_VIEW,
                  ]}
                >
                  <ValidatePermitRejectDispatchDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "re-permit-vendor",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.VENDOR, ROLE.VENDOR_VIEW]}>
                  <RePermitVendor keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.VENDOR, ROLE.VENDOR_VIEW]}>
                  <RePermitVendorDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "request-open-access-funnel-project",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.FUNNEL_PROJECT, ROLE.ADMINISTRATOR]}>
                  <RequestOpenAccessFunnelProject keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.FUNNEL_PROJECT, ROLE.ADMINISTRATOR]}>
                  <RequestOpenAccessFunnelProjectDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "request-re-permit-funnel-project",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.FUNNEL_PROJECT]}>
                  <RequestRePermitFunnelProject keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.FUNNEL_PROJECT]}>
                  <RequestRePermitFunnelProjectDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "change-request-re-permit-funnel-management",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.FUNNEL_MANAGEMENT, ROLE.ADMINISTRATOR]}>
                  <ChangeRequestRePermitFunnelManagement keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.FUNNEL_MANAGEMENT, ROLE.ADMINISTRATOR]}>
                  <ChangeRequestRePermitFunnelManagementDetail
                    keycloak={keycloak}
                  />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "monitoring-suffix",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate
                  roles={[
                    ROLE.FUNNEL_PROJECT,
                    ROLE.PERMIT_DISPATCH,
                    ROLE.PERMIT_MONITORING,
                    ROLE.PROJECT_OPERATIONAL_LEAD,
                    ROLE.PROJECT_MANAGER,
                  ]}
                >
                  <MonitoringSuffix keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate
                  roles={[
                    ROLE.FUNNEL_PROJECT,
                    ROLE.PERMIT_DISPATCH,
                    ROLE.PERMIT_MONITORING,
                    ROLE.PROJECT_OPERATIONAL_LEAD,
                    ROLE.PROJECT_MANAGER,
                  ]}
                >
                  <MonitoringSuffixDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: "design-lastmile",
    children: [
      {
        path: "dashboard-apd",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate
                  roles={[
                    ROLE.VENDOR,
                    ROLE.LM_FEEDER_DESIGN,
                    ROLE.BB_DESIGN,
                    ROLE.COSTING,
                    ROLE.APD_VIEW,
                    ROLE.VENDOR_VIEW,
                  ]}
                >
                  <DashboardLinknetAPD keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "network-type",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate
                  roles={[
                    ROLE.ADMINISTRATOR,
                    ROLE.LM_FEEDER_DESIGN,
                    ROLE.APD_VIEW,
                  ]}
                >
                  <NetworkTypeLMFeederList keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate
                  roles={[
                    ROLE.ADMINISTRATOR,
                    ROLE.LM_FEEDER_DESIGN,
                    ROLE.APD_VIEW,
                  ]}
                >
                  <NetworkTypeLMFeederDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "apd-list",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate
                  roles={[
                    ROLE.ADMINISTRATOR,
                    ROLE.VENDOR,
                    ROLE.LM_FEEDER_DESIGN,
                    ROLE.COSTING,
                    ROLE.BB_DESIGN,
                    ROLE.APD_VIEW,
                    ROLE.VENDOR_VIEW,
                  ]}
                >
                  <ApdVendorList keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate
                  roles={[
                    ROLE.ADMINISTRATOR,
                    ROLE.VENDOR,
                    ROLE.LM_FEEDER_DESIGN,
                    ROLE.COSTING,
                    ROLE.BB_DESIGN,
                    ROLE.APD_VIEW,
                    ROLE.VENDOR_VIEW,
                  ]}
                >
                  <ApdVendorDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "validate-olt",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.LM_FEEDER_DESIGN]}>
                  <ApdLMFeederList keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "request-code-fdt-olt",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.ADMINISTRATOR, ROLE.VENDOR]}>
                  <RequestCodeFdtOlt keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "define-permit-feeder",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.VENDOR, ROLE.VENDOR_VIEW]}>
                  <DefinePermitFeeder keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.VENDOR, ROLE.VENDOR_VIEW]}>
                  <DefinePermitFeederDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "reboundary",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.LM_FEEDER_DESIGN, ROLE.APD_VIEW]}>
                  <Reboundary keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.LM_FEEDER_DESIGN, ROLE.APD_VIEW]}>
                  <ReboundaryDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "request-reboundary",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.VENDOR, ROLE.VENDOR_VIEW]}>
                  <RequestReboundary keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: "create",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.VENDOR]}>
                  <RequestReboundaryCreate keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.VENDOR, ROLE.VENDOR_VIEW]}>
                  <RequestReboundaryDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "rollback-all-list",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate
                  roles={[
                    ROLE.LM_FEEDER_DESIGN,
                    ROLE.COSTING,
                    ROLE.BB_DESIGN,
                    ROLE.FUNNEL_PROJECT,
                    ROLE.VENDOR,
                    ROLE.APD_VIEW,
                    ROLE.VENDOR_VIEW,
                  ]}
                >
                  <RollbackAllList keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate
                  roles={[
                    ROLE.LM_FEEDER_DESIGN,
                    ROLE.COSTING,
                    ROLE.BB_DESIGN,
                    ROLE.FUNNEL_PROJECT,
                    ROLE.VENDOR,
                    ROLE.APD_VIEW,
                    ROLE.VENDOR_VIEW,
                  ]}
                >
                  <RollbackAllDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: "apd-lm",
            children: [
              {
                path: ":id",
                element: (
                  <Suspense fallback={<Loading />}>
                    <RoleGate
                      roles={[
                        ROLE.LM_FEEDER_DESIGN,
                        ROLE.COSTING,
                        ROLE.BB_DESIGN,
                        ROLE.FUNNEL_PROJECT,
                        ROLE.VENDOR,
                        ROLE.APD_VIEW,
                        ROLE.VENDOR_VIEW,
                      ]}
                    >
                      <RollbackAPDLMDetail keycloak={keycloak} />
                    </RoleGate>
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "wo-management",
    children: [
      {
        path: "nosa",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.NOSA, ROLE.WO_VIEW]}>
                  <WONosaList keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: "create",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.NOSA]}>
                  <WONosaCreate keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.NOSA, ROLE.WO_VIEW]}>
                  <WONosaDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "enterprise",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.ENTERPRISE, ROLE.WO_VIEW]}>
                  <WOEnterpriseList keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: "create",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.ENTERPRISE]}>
                  <WOEnterpriseCreate keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <RoleGate roles={[ROLE.ENTERPRISE, ROLE.WO_VIEW]}>
                  <WOEnterpriseDetail keycloak={keycloak} />
                </RoleGate>
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  ...(!hideFeatures.includes("split") ? e2eSplitRoutes : []),
  {
    path: "preview",
    children: [
      {
        path: ":path/:name",
        element: (
          <Suspense fallback={<Loading />}>
            <Preview />
          </Suspense>
        ),
      },
    ],
  },
];
